/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import {
  UiController,
  breakdownList,
  commands,
  compareWindows,
  consoleCommands,
  events,
  paceVariableNames,
} from 'modules/common/ui-controller';
import { getHotelValuesFromList } from 'modules/common/utils/array';
import { KEYS } from 'modules/dashboard/constants';
import { dashboardActions } from 'modules/dashboard/slice';
import {
  CURRENCY_STEP,
  PMS_SYNC_STEP,
  VI_RESPONSE_STEP,
  createVisualCollapseStep,
} from 'modules/common/ui-controller/utils/shared-command-steps';
import { summaryActions } from '../slice';
import { setCompareWindowCommandValue } from '../../inner-filter/functions';

const isLoaded = (context) => context.isLoaded;
// a secondary hotel switches the summary to the hotel comparison visual, this wait lets that visual
// replace the current one so the following commands (normalized view) are applied on the new visual
const COMPARE_HOTEL_DELAY = 4000;
/**
 * Summary tab filter commands in the order they are applied.
 * The position in this list is the execution order, see useCommandPipeline.
 * context is built in useSummaryCommandPipeline.
 */
const SUMMARY_FILTER_COMMAND_STEPS = [
  {
    command: consoleCommands.BOOKING_DATE,
    event: events.SET_SUMMARY_CUSTOM_BOOKING,
    // open custom booking date picker, the date range is applied once it is opened
    prepare: (value, { dispatch }) => {
      if (value !== commands.OTB) {
        dispatch(summaryActions.setSummaryOtb(true));
      }
    },
    ready: (context, { pending }) =>
      context.isLoaded &&
      (pending[consoleCommands.BOOKING_DATE] === commands.OTB || Boolean(context.customBooking)),
    apply: (value, { dispatch, resetBookingDate }) => {
      if (value === commands.OTB) {
        dispatch(summaryActions.setSummaryOtb(false));
        dispatch(summaryActions.setSummaryBookingDate(resetBookingDate));
      } else {
        // emit set custom booking date range event
        UiController.setBookingDateRange(value);
      }
    },
  },
  {
    command: consoleCommands.SEGMENT,
    event: events.SET_SUMMARY_BREAKDOWN,
    ready: (context) => context.isLoaded && context.chartBreakdownList?.length > 0,
    apply: (value, { dispatch, chartBreakdownList }) => {
      // get breakdown option value
      const breakdownOption = breakdownList[value?.substring(0, 2)];
      // get isDetailed state from command
      const detailsStatus = parseInt(value?.substring(2, 4), 10);
      if (breakdownOption !== undefined) {
        const option =
          breakdownOption === 'Travel Agent'
            ? chartBreakdownList?.filter((val) => val?.label === 'Consortia / IATA')
            : chartBreakdownList?.filter((val) => val?.label?.includes(breakdownOption));
        if (option?.length > 0) {
          dispatch(summaryActions.setBreakdown(option[0]?.id));
        }
        // set isDetailed  state
        if (!Number.isNaN(detailsStatus)) {
          dispatch(summaryActions.setIsDetailed(detailsStatus === 1));
        }
      }
    },
  },
  {
    command: consoleCommands.COMPARE_HOTEL,
    event: events.SET_SUMMARY_COMPARE_HOTELS,
    ready: (context) => context.isLoaded && context.hotelList?.length > 0,
    apply: (value, { dispatch, hotelList }) => {
      const hotelsSelected = getHotelValuesFromList(value, hotelList);
      if (hotelsSelected?.length > 0) {
        dispatch(summaryActions.setEnableSecondaryHotelGroups(false));
        dispatch(summaryActions.setHotelFocusOn(hotelsSelected[0]));
      }
    },
    delayAfter: COMPARE_HOTEL_DELAY,
  },
  {
    command: consoleCommands.COMPARE_HOTEL_GROUP,
    event: events.SET_SUMMARY_HOTEL_GROUP,
    ready: (context) => context.isLoaded && context.hotelGroupList?.length > 0,
    apply: (value, { dispatch, hotelGroupList }) => {
      const hotelGroupsSelected = getHotelValuesFromList(value, hotelGroupList);
      if (hotelGroupsSelected?.length > 0) {
        dispatch(summaryActions.setEnableSecondaryHotelGroups(true));
        dispatch(summaryActions.setHotelFocusOn(hotelGroupsSelected[0]));
      }
    },
    delayAfter: COMPARE_HOTEL_DELAY,
  },
  {
    command: consoleCommands.NORMALIZED_VIEW,
    event: events.SET_SUMMARY_NORMALIZED_VIEW,
    ready: isLoaded,
    apply: (value, { dispatch }) => {
      // execute dispatch function to set normalized view with command value
      dispatch(summaryActions.setNormalizedView(value === 1));
    },
  },
  {
    command: consoleCommands.ALIGNMENT,
    event: events.SUMMARY_ALIGNMENT,
    ready: isLoaded,
    apply: (value, { handleToggleChange }) => {
      handleToggleChange({ target: { checked: value } });
    },
  },
  {
    command: consoleCommands.SYMMETRIC,
    event: events.SET_SUMMARY_SYMMETRIC_COMPARISON,
    ready: isLoaded,
    apply: (value, { dispatch }) => {
      dispatch(summaryActions.setSymmetricComparisonToggle(value === 1));
    },
  },
  {
    // custom dates are applied by the next step, only with the custom compare window
    command: consoleCommands.COMPARE_WINDOW,
    requires: [consoleCommands.CUSTOM_DATES],
    event: events.SET_SUMMARY_COMPARE_WINDOW,
    ready: (context) => context.isLoaded && Boolean(context.dateComparison),
    apply: (value, { customBooking, handleComparisonChange }) => {
      setCompareWindowCommandValue(value, customBooking, handleComparisonChange);
      return compareWindows[value] === KEYS.CUSTOM
        ? [consoleCommands.COMPARE_WINDOW]
        : [consoleCommands.COMPARE_WINDOW, consoleCommands.CUSTOM_DATES];
    },
  },
  {
    command: consoleCommands.CUSTOM_DATES,
    event: events.SET_SUMMARY_CUSTOM_DATE_RANGE,
    ready: (context, { keys }) =>
      context.isLoaded &&
      (context.dateComparison === KEYS.CUSTOM || !keys.includes(consoleCommands.COMPARE_WINDOW)),
    apply: (value, { dispatch, dateComparison }) => {
      if (dateComparison === KEYS.CUSTOM) {
        const datesExtracted = value?.split('-');
        dispatch(summaryActions.setSummaryCustomDate(datesExtracted[0]));
        // emit event to set custom date range
        UiController.setCustomDateRange(value);
      }
    },
  },
  {
    command: consoleCommands.SYMMETRIC_COMPARISON_COLLAPSE,
    event: events.SET_SUMMARY_SYMMETRIC_COMPARISON_COLLAPSE,
    ready: isLoaded,
    apply: (value, { dispatch }) => {
      dispatch(summaryActions.setSymmetricComparisonIsDisplay(value === 1));
    },
  },
  {
    command: consoleCommands.FORECAST,
    event: events.SET_SUMMARY_FORECAST,
    ready: isLoaded,
    apply: (value, { dispatch, organization, isHotelForecast }) => {
      // forecast filter is available only for organizations that have it enabled
      if (organization?.enableForecastFilter) {
        dispatch(dashboardActions.setSummaryForecastFilter(isHotelForecast && value === 1));
      }
    },
  },
  {
    command: consoleCommands.PACE_HORIZON,
    event: events.SET_SUMMARY_PACE_HORIZON,
    ready: (context) => context.isLoaded && context.paceList?.length > 0,
    apply: (value, { dispatch, paceList }) => {
      const paces = paceList?.filter((pace) => pace?.label[0].includes(value));
      if (paces?.length > 0) {
        dispatch(dashboardActions.setPaceHorizon(paces[0]?.id));
      }
    },
  },
  {
    command: consoleCommands.PACE_VARIABLE,
    event: events.SET_SUMMARY_PACE_VARIABLE,
    ready: isLoaded,
    apply: (value, { dispatch }) => {
      const variable = paceVariableNames[value];
      if (variable !== undefined) {
        dispatch(dashboardActions.setPaceVariable(variable));
      }
    },
  },
  {
    command: consoleCommands.EXTENDING_FORECAST,
    event: events.SET_EXTENDING_SUMMARY_FORECAST,
    ready: isLoaded,
    apply: (value, { dispatch }) => {
      dispatch(summaryActions.setSummaryVisualToggle(value === 1));
    },
  },
];

// time the summary visuals stay hidden before the visual collapse command value is applied
const VISUAL_COLLAPSE_DELAY = 4000;
// actions to show or hide the summary visuals
const VISUAL_DISPLAY_ACTIONS = {
  PA: summaryActions.setPaceVisualIsDisplay,
  RV: summaryActions.setRevenueSetIsDisplay,
  CX: summaryActions.setCxlSetIsDisplay,
};
/**
 * Summary tab commands of a command url in the order they are applied
 */
const SUMMARY_COMMAND_STEPS = [
  ...SUMMARY_FILTER_COMMAND_STEPS,
  CURRENCY_STEP,
  PMS_SYNC_STEP,
  VI_RESPONSE_STEP,
  createVisualCollapseStep(
    events.SET_SUMMARY_VISUAL_COLLAPSE,
    VISUAL_COLLAPSE_DELAY,
    () => VISUAL_DISPLAY_ACTIONS
  ),
];

export default SUMMARY_COMMAND_STEPS;
